import { Transform, TransformPosition, TransformRotation } from '@ikea-aoa/elements-3d';
import {
	ConnectionRelation,
	ConnectableObject,
	ConnectedObjects,
} from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import {
	ConnectionManager,
	TransformVector,
	GridTools,
	GridBox,
} from '@ikea-aoa/component-view-3d';
import Thresholds from './SnappingGrid/Thresholds';
import { ModuleConnectionManager } from './ModuleConnectionManager';
import { ConnectionKitsManager } from './ConnectionKitsManager';

export interface SnappingPoint {
	articleId: string;
	transform: Transform;
	gridBoxes: GridBox[];
	isValid: boolean;
	isValidLine: boolean;
	connection?: Connection;
	intersections: { id: string; gridBox: GridBox }[];
}

export interface DraggedObjectData {
	closest: SnappingPoint | null;
	closestValid: SnappingPoint | null;
	closestConnection: SnappingPoint | null;
	intersections: string[];
}

export interface Connection {
	parentId: string;
	relation: ConnectionRelation;
}

export class SnappingGrid {
	private gridTools: GridTools;
	private gridBoxes: Map<string, GridBox>;
	private draggedGridBox: { articleId: string; gridBox: GridBox };

	private _connectionSnappingPoints: SnappingPoint[];
	private _gridSnappingPoints: SnappingPoint[];

	constructor(
		private connectionManager: ConnectionManager,
		private moduleManager: ModuleConnectionManager,
		private connectionKitsManager: ConnectionKitsManager,
		private sceneItems: Map<string, ConnectableObject>,
		private autoProducts: Array<string>,
		private isModular: boolean
	) {
		this.gridTools = new GridTools(sceneItems, connectionManager);
	}

	public getConnectionSnappingPoints(): SnappingPoint[] {
		return this._connectionSnappingPoints;
	}

	public getGridSnappingPoints(): SnappingPoint[] {
		return this._gridSnappingPoints;
	}

	public getGridBoxes(): Map<string, GridBox> {
		return this.gridBoxes;
	}

	public generate(sceneObject: ConnectableObject, useSimpleConnectKitsLogic: boolean): void {
		this.clear();

		const allChildren = this.connectionManager.getAllChildren(sceneObject.id);
		const items = new Map<string, ConnectableObject>();
		const sceneObjectModelData = this.connectionManager.getModelData(sceneObject.articleId);

		this.sceneItems.forEach((item, id) => {
			const isAutoObject = this.isAutoObject(item);

			if (
				sceneObjectModelData &&
				!sceneObjectModelData.logic?.excludeFromAutoCheck &&
				(isAutoObject || id == sceneObject.id || allChildren.includes(id))
			) {
				return;
			}
			items.set(id, item);
		});

		this.gridBoxes = this.gridTools.createGridBoxes(items);
		this._connectionSnappingPoints = this.calculateConnectionPoints(
			sceneObject,
			items,
			this.gridBoxes,
			useSimpleConnectKitsLogic,
			false,
			false
		);
		const allItemsAreSeparate = Array.from(items.values()).every((item) => {
			return this.connectionManager.isSeparate(item.articleId);
		});
		this._gridSnappingPoints =
			this.connectionManager.isSeparate(sceneObject.articleId) || allItemsAreSeparate
				? this.calculateGridPoints(sceneObject, this.gridBoxes)
				: [];
	}

	public clear(): void {
		this._connectionSnappingPoints = [];
		this._gridSnappingPoints = [];
		if (this.gridBoxes) {
			this.gridBoxes.clear();
		}
	}

	public getDraggedObjectData(
		sceneObject: ConnectableObject,
		onlyParentId?: string
	): DraggedObjectData {
		let d = Number.MAX_VALUE;
		let vd = Number.MAX_VALUE;
		let cd = Number.MAX_VALUE;

		let closest: SnappingPoint | null = null;
		let closestValid: SnappingPoint | null = null;
		let closestConnection: SnappingPoint | null = null;
		const { transform } = sceneObject;

		const isSeparate = this.canBePlacedSeparetly(sceneObject);
		let snappingPoints: SnappingPoint[];
		if (isSeparate) {
			snappingPoints = [...this._connectionSnappingPoints, ...this._gridSnappingPoints];
		} else {
			snappingPoints = this._connectionSnappingPoints;
		}

		// check intersection
		const draggedGridBox = this.getDraggedObjectGridBox(sceneObject);
		const intersections = this.gridTools.checkBoxIntersections(draggedGridBox, this.gridBoxes);

		const parentModelData = this.connectionManager.getModelData(sceneObject.articleId);
		if (parentModelData && parentModelData.modelId === 'JATTEBO_1_5_SEAT') {
			snappingPoints = snappingPoints.filter(
				(point) => point.connection?.relation.type !== 'notPrioritized'
			);
		}

		// get closest snapping point
		snappingPoints.forEach((snappingPoint) => {
			if (!onlyParentId || snappingPoint.connection?.parentId === onlyParentId) {
				const { isValid, isValidLine, intersections, connection } = snappingPoint;
				const a = transform.position;
				const b = snappingPoint.transform.position;
				const valid = isValid && isValidLine && !intersections.length;
				const distance = TransformVector.distanceSquared(
					a as TransformPosition,
					b as TransformPosition
				);
				if (distance < d) {
					d = distance;
					closest = snappingPoint;
				}
				// Used for when armrest position not available
				const connectToBackClamp =
					snappingPoint.connection?.relation.parent === 'BackClamp' && !intersections.length;
				if (
					valid &&
					distance < vd &&
					(!sceneObject.addedWithClick ||
						snappingPoint.connection?.relation.type !== 'notPrioritized' ||
						connectToBackClamp)
				) {
					vd = distance;
					closestValid = snappingPoint;
				}
				if (connection && !intersections.length && distance < cd) {
					cd = distance;
					closestConnection = snappingPoint;
				}
			}
		});

		// set priority to a connection points
		if (closestConnection && closestValid) {
			const newClosestConnection = closestConnection as SnappingPoint;
			const newClosestValid = closestValid as SnappingPoint;
			if (newClosestConnection.isValid && !newClosestConnection.intersections.length) {
				const connectionTransform = newClosestConnection.transform as Transform;
				const validTransform = newClosestValid.transform as Transform;
				const a = connectionTransform.position || TransformVector.zero();
				const b = validTransform.position || TransformVector.zero();
				const distance = TransformVector.distanceSquared(a, b);
				if (distance < 2) {
					closestValid = newClosestConnection;
				}
			}
		}

		if (sceneObject.addedWithClick) {
			sceneObject.addedWithClick = false;
		}

		return {
			closest,
			closestValid,
			closestConnection,
			intersections,
		};
	}

	public calculateConnectionPoints(
		sceneObject: ConnectableObject,
		items: Map<string, ConnectableObject>,
		gridBoxes: Map<string, GridBox>,
		useSimpleConnectKitsLogic = false,
		includeChildOnly = false,
		checkAvailable = true,
		isSmartMirror = false
	): Array<SnappingPoint> {
		const filter = {
			valid: false,
			available: checkAvailable,
		};

		// connections relation map
		const relationMap = this.connectionManager.getAllRelations(sceneObject, filter, this.isModular);
		const invalidPositions: TransformPosition[] = [];
		const articleId = sceneObject.articleId;
		const isSeparate = this.connectionManager.isSeparate(articleId);
		const isChildOnly = this.connectionManager.isChildOnly(articleId);
		const isSofabed = this.connectionManager.getFamily(articleId) === 'SOFABED';
		const hasChildren = this.connectionManager.hasChildren(sceneObject.id);
		const allChildObjects = hasChildren
			? includeChildOnly
				? this.connectionManager
						.getAllChildOnlyChildren(sceneObject.id)
						.map((id) => this.sceneItems.get(id))
				: this.connectionManager.getAllChildObjects(sceneObject)
			: [];

		// check all intersections and create snapping points
		const snappingPoints = new Array<SnappingPoint>();
		relationMap.forEach((relationsArray, parentId) => {
			const parent = items.get(parentId);
			if (parent) {
				this.handleMaxNumberOfConnections(isSmartMirror, parent, relationsArray);

				const standaloneParent = this.connectionManager.getStandaloneParent(parent);
				const threshold = this.getThreshold(
					parent,
					standaloneParent,
					isChildOnly,
					isSeparate,
					isSofabed
				);
				const hasConnectionKits = this.connectionKitsManager.hasAvailableConnectionKits(
					articleId,
					parent,
					sceneObject,
					useSimpleConnectKitsLogic
				);

				relationsArray.forEach((relation) => {
					// check get connected world transform
					const localTransform = this.connectionManager.getConnectedTransform(
						articleId,
						parent.articleId,
						relation
					);
					const worldTransform = this.connectionManager.getRelativeWorldTransformFromObject(
						localTransform,
						parent
					);

					// check connection kits and isValid
					const isValid = this.isValidConnection(relation, hasConnectionKits, parent, articleId);
					if (!isValid) {
						invalidPositions.push(worldTransform.position as TransformPosition);
					}

					// create connected grid box
					const bounds = this.gridTools.getBoundsName(articleId);
					if (bounds) {
						snappingPoints.push(
							this.createSnappingPoint(
								articleId,
								worldTransform,
								bounds,
								threshold,
								isValid,
								parentId,
								relation,
								hasChildren,
								sceneObject,
								includeChildOnly,
								gridBoxes,
								parent,
								standaloneParent,
								localTransform,
								allChildObjects,
								items
							)
						);
					}
				});
			}
		});

		this.checkSnappingPoints(snappingPoints, invalidPositions);

		return snappingPoints;
	}

	public calculateGridPoints(
		sceneObject: ConnectableObject,
		gridBoxes: Map<string, GridBox>
	): Array<SnappingPoint> {
		const children = this.connectionManager.getChildren(sceneObject.id).filter((c) => {
			const sceneItem = this.sceneItems.get(c);
			if (sceneItem) {
				return sceneItem.articleId !== 'leg';
			}
			return false;
		});
		const rotation = this.connectionManager.getWorldRotation(sceneObject);

		const stepSize = this.isModular ? 8 : 4;

		// create grid boxes and snapping positions
		const points = this.gridTools.getAllSnappingPositions(
			sceneObject,
			stepSize,
			3,
			Array.from(gridBoxes.keys()) // change to scene items?
		);

		// check intersection and create snapping points
		const snappingPoints = new Array<SnappingPoint>();
		points.forEach((pointsArray, id) => {
			pointsArray.forEach((points) => {
				let isValidLine = true;
				points.forEach((pos) => {
					// fix point Y-position
					const position = TransformVector.clone(pos);
					position.y = this.getArticlePositionY(sceneObject.articleId);
					const transform = {
						position,
						rotation,
					};
					const bounds = this.gridTools.getBoundsName(sceneObject.articleId);
					if (bounds) {
						const box = this.gridTools.createGridBox(
							sceneObject.articleId,
							transform,
							bounds,
							-1.5
						);
						const snappingPoint: SnappingPoint = {
							articleId: sceneObject.articleId,
							transform,
							gridBoxes: [box],
							isValid: true,
							isValidLine: true,
							intersections: [],
						};
						gridBoxes.forEach((gridBox, id) => {
							if (this.gridTools.intersects(gridBox, box)) {
								snappingPoint.intersections.push({ id, gridBox });
							}
						});

						children.forEach((c) => {
							const child = this.sceneItems.get(c);
							if (child && child.transform) {
								const world = this.connectionManager.getRelativeWorldTransform(
									child.transform,
									transform
								);
								const bounds = this.gridTools.getBoundsName(child.articleId);
								if (bounds) {
									const box = this.gridTools.createGridBox(child.articleId, world, bounds, 0.15);
									snappingPoint.gridBoxes.push(box);
									gridBoxes.forEach((gridBox) => {
										if (this.gridTools.intersects(gridBox, box)) {
											snappingPoint.intersections.push({ id, gridBox });
										}
									});
								}
							}
						});
						if (!isValidLine || snappingPoint.intersections.length) {
							isValidLine = false;
							snappingPoint.isValidLine = false;
						}

						snappingPoints.push(snappingPoint);
					}
				});
			});
		});

		return snappingPoints;
	}

	public canBePlacedSeparetly(sceneObject: ConnectableObject): boolean {
		const isSeparate = this.connectionManager.isSeparate(sceneObject.articleId);
		const isAddAsChild = this.connectionManager.isAddAsChild(sceneObject.articleId);
		if (
			isSeparate ||
			(!isAddAsChild && this.connectionManager.getAllStandaloneObjects().length == 1)
		) {
			return true;
		}
		return false;
	}

	private handleMaxNumberOfConnections(
		isSmartMirror: boolean,
		parent: ConnectableObject,
		relationsArray: ConnectionRelation[]
	) {
		// Checks model logic maxNumberOfConnections
		if (!isSmartMirror) {
			const parentModelData = this.connectionManager.getModelData(parent.articleId);
			if (parentModelData && parentModelData.logic?.maxNumberOfConnections) {
				const connectedModules = this.moduleManager.getConnectedModules(parent);
				if (connectedModules.length >= parentModelData.logic?.maxNumberOfConnections) {
					relationsArray.length = 0;
				}
			}
		}
	}

	private isValidConnection(
		relation: ConnectionRelation,
		hasConnectionKits: boolean,
		parent: ConnectableObject,
		articleId: string
	) {
		let isValid = this.connectionManager.isValid(relation) && hasConnectionKits;
		// restrict connection to already attached footstool for non modular sofas
		if (!this.isModular) {
			const addAsChild = this.connectionManager.isAddAsChild(parent.articleId);
			const isSeparate = this.connectionManager.isSeparate(parent.articleId);
			const childFamily = this.connectionManager.getFamily(articleId);
			const parentFamily = this.connectionManager.getFamily(parent.articleId);
			if (addAsChild && (isSeparate || childFamily === 'OPENEND' || parentFamily === 'OPENEND')) {
				const connectedModules = this.moduleManager.getConnectedModules(parent);
				if (connectedModules.length || parent.parentId) {
					isValid = false;
				}
			}
		}
		return isValid;
	}

	private getThreshold(
		parent: ConnectableObject,
		standaloneParent: ConnectableObject,
		isChildOnly: boolean,
		isSeparate: boolean,
		isSofabed: boolean
	) {
		const toCorner = this.connectionManager.getFamily(parent.articleId) === 'CORNER';
		const isDeepChildOnly = isChildOnly && parent !== standaloneParent;

		const threshold = isDeepChildOnly
			? // threshold for deep child only object expl. cushins.
			  Thresholds.deepChildOnly
			: // thresholds for child
			isChildOnly
			? Thresholds.child
			: // thresholds for separate
			isSeparate
			? this.isModular
				? Thresholds.default
				: Thresholds.separate
			: // thresholds when connection to corner
			toCorner && !isSofabed
			? Thresholds.toCorner
			: Thresholds.default;
		return threshold;
	}

	private checkSnappingPoints(
		snappingPoints: SnappingPoint[],
		invalidPositions: TransformPosition[]
	) {
		// Set the snappingPoint to invalid if it's at a same position as another invalid snappingPoint
		snappingPoints.forEach((point) => {
			if (
				invalidPositions.find((pos) =>
					TransformVector.isEqualWithEpsilon(
						pos,
						point.transform.position as TransformPosition,
						0.001
					)
				)
			) {
				point.isValid = false;
			}
		});
	}

	private createSnappingPoint(
		articleId: string,
		worldTransform: Transform,
		bounds: string,
		threshold: { width: number; depth: number },
		isValid: boolean,
		parentId: string,
		relation: ConnectionRelation,
		hasChildren: boolean,
		sceneObject: ConnectableObject,
		includeChildOnly: boolean,
		gridBoxes: Map<string, GridBox>,
		parent: ConnectableObject,
		standaloneParent: ConnectableObject,
		localTransform: Transform,
		allChildObjects: (ConnectableObject | undefined)[],
		items: Map<string, ConnectableObject>
	) {
		const connectedBox = this.gridTools.createGridBox(articleId, worldTransform, bounds, threshold);

		// create snapping point
		const snappingPoint: SnappingPoint = {
			articleId,
			transform: worldTransform,
			gridBoxes: [connectedBox],
			isValid,
			isValidLine: true,
			connection: {
				parentId,
				relation,
			},
			intersections: [],
		};

		const childGridBoxes = this.getChildGridBoxes(
			hasChildren,
			sceneObject,
			worldTransform,
			includeChildOnly,
			snappingPoint
		);
		const intersections = this.checkIntersections(
			snappingPoint,
			gridBoxes,
			parent,
			standaloneParent,
			connectedBox,
			childGridBoxes,
			articleId
		);
		const autoGridBoxes = this.predictAutoObjectsGridBoxes(
			sceneObject,
			articleId,
			parentId,
			localTransform,
			allChildObjects,
			relation,
			items,
			snappingPoint,
			gridBoxes,
			intersections,
			childGridBoxes,
			connectedBox
		);

		this.checkAutoObjectsIntersectionBetweenEachOther(autoGridBoxes, sceneObject, intersections);

		const snappingPointModelData = this.connectionManager.getModelData(snappingPoint.articleId);
		const parentModelData = this.connectionManager.getModelData(parent.articleId);
		if (
			snappingPoint &&
			snappingPointModelData &&
			parentModelData &&
			snappingPointModelData.logic?.excludeFromAutoCheck &&
			!parentModelData.modelId?.includes('BACKREST')
		) {
			const snappingPointParentConnection = snappingPoint.connection?.relation.parent;
			const parentConnections = this.moduleManager.getAllConnectedModules(parent.id);
			const parentOccupied = this.connectionManager.getOccupiedConnections(
				parentId,
				parent.articleId
			);

			const occupiedConnectionsOnParent = [
				...parentOccupied,
				...parentConnections.map((conn) => {
					return conn.connection.from;
				}),
			];

			if (
				(snappingPointParentConnection?.includes('Left') &&
					occupiedConnectionsOnParent.includes('LeftClamp')) ||
				(snappingPointParentConnection?.includes('Right') &&
					occupiedConnectionsOnParent.includes('RightClamp'))
			) {
				snappingPoint.isValid = false;
			}
		}

		return snappingPoint;
	}

	private getChildGridBoxes(
		hasChildren: boolean,
		sceneObject: ConnectableObject,
		worldTransform: Transform,
		includeChildOnly: boolean,
		snappingPoint: SnappingPoint
	) {
		const childGridBoxes = hasChildren
			? this.getChildrenGridBoxes(sceneObject.id, worldTransform, includeChildOnly)
			: new Map<string, GridBox>();
		childGridBoxes.forEach((b) => snappingPoint.gridBoxes.push(b));
		return childGridBoxes;
	}

	private checkAutoObjectsIntersectionBetweenEachOther(
		autoGridBoxes: Map<string, GridBox[]>,
		sceneObject: ConnectableObject,
		intersections: { id: string; gridBox: GridBox }[]
	) {
		if (autoGridBoxes.get(sceneObject.id)) {
			const autoGridBox = autoGridBoxes.get(sceneObject.id);
			if (autoGridBox) {
				autoGridBox.forEach((box) => {
					autoGridBoxes.forEach((array, parentId) => {
						if (parentId == sceneObject.id) {
							// do not check own auto objects
							return;
						}
						array.forEach((gridbox) => {
							if (this.gridTools.intersects(box, gridbox)) {
								intersections.push({ id: parentId, gridBox: gridbox });
							}
						});
					});
				});
			}
		}
	}

	private predictAutoObjectsGridBoxes(
		sceneObject: ConnectableObject,
		articleId: string,
		parentId: string,
		localTransform: Transform,
		allChildObjects: (ConnectableObject | undefined)[],
		relation: ConnectionRelation,
		items: Map<string, ConnectableObject>,
		snappingPoint: SnappingPoint,
		gridBoxes: Map<string, GridBox>,
		intersections: { id: string; gridBox: GridBox }[],
		childGridBoxes: Map<string, GridBox>,
		connectedBox: GridBox
	) {
		const mockConnectedItem = {
			id: sceneObject.id,
			articleId,
			parentId,
			transform: localTransform,
		} as ConnectableObject;
		const mockItems = [mockConnectedItem, ...allChildObjects];
		const mockConnections: Array<ConnectedObjects> = [
			{ parentId, childId: sceneObject.id, relation },
		];
		const autoGridBoxes = this.getAutoObjectsGridBoxes(
			items,
			mockItems as ConnectableObject[],
			mockConnections
		);
		autoGridBoxes.forEach((array, parentId) => {
			if (parentId == sceneObject.id) {
				snappingPoint.gridBoxes.push(...array);
			}
			const connected = this.connectionManager.getDirectlyConnected(parentId);
			const ignore = [parentId, ...connected];
			// check intersections of auto-objects and other grid boxes
			let int = this.gridTools.checkMultyBoxesIntersections(array, gridBoxes, ignore);
			int.forEach((id) => intersections.push({ id, gridBox: gridBoxes.get(id) as GridBox }));
			// check intersections with connected object and it's children
			if (parentId == sceneObject.id) {
				// do not check own auto objects
				return;
			}
			childGridBoxes.set(sceneObject.id, connectedBox);
			int = this.gridTools.checkMultyBoxesIntersections(array, childGridBoxes);
			int.forEach((id) => intersections.push({ id, gridBox: gridBoxes.get(id) as GridBox }));
		});
		return autoGridBoxes;
	}

	private checkIntersections(
		snappingPoint: SnappingPoint,
		gridBoxes: Map<string, GridBox>,
		parent: ConnectableObject,
		standaloneParent: ConnectableObject,
		connectedBox: GridBox,
		childGridBoxes: Map<string, GridBox>,
		articleId: string
	) {
		// check itersections for main object
		const intersections = snappingPoint.intersections;
		const modelData = this.connectionManager.getModelData(snappingPoint.articleId);
		gridBoxes.forEach((gridBox, id) => {
			const sceneItem = this.sceneItems.get(id);
			if (modelData && modelData.logic?.excludeFromAutoCheck && sceneItem?.parentId !== parent.id) {
				return;
			}
			// ignore parent
			if (id == parent.id || id == standaloneParent.id) {
				return;
			}
			// check intersection
			if (this.gridTools.intersects(gridBox, connectedBox)) {
				intersections.push({ id, gridBox });
			}
		});

		// check intersections for child objects
		gridBoxes.forEach((gridBox, id) => {
			childGridBoxes.forEach((childBox) => {
				// ignore parent for deep childOnly children. TODO remove this logic when we add y-top, y-bot props for gridBox.
				if (
					this.connectionManager.isChildOnly(articleId) &&
					(id == parent.id || id == standaloneParent.id)
				) {
					return;
				}
				if (this.gridTools.intersects(gridBox, childBox)) {
					intersections.push({ id, gridBox });
				}
			});
		});
		return intersections;
	}

	private getDraggedObjectGridBox(sceneObject: ConnectableObject): GridBox {
		const { articleId, transform } = sceneObject;
		// create new
		if (!this.draggedGridBox || this.draggedGridBox.articleId !== articleId) {
			const bounds = this.gridTools.getBoundsName(sceneObject.articleId);
			if (bounds) {
				const gridBox = this.gridTools.createGridBoxFromObject(sceneObject, bounds, 4);
				this.draggedGridBox = { articleId, gridBox };
				return gridBox;
			}
		}
		// updates
		if (
			!TransformVector.isEqual(
				transform.position as TransformPosition,
				this.draggedGridBox.gridBox.position
			) ||
			!TransformVector.isEqual(
				transform.rotation as TransformRotation,
				this.draggedGridBox.gridBox.rotation
			)
		) {
			this.draggedGridBox.gridBox.setTransform(transform);
			return this.draggedGridBox.gridBox;
		}
		this.draggedGridBox.gridBox.calculateBoundingBox();
		return this.draggedGridBox.gridBox;
	}

	private getChildrenGridBoxes(
		parentId: string,
		worldTransform: Transform,
		includeChildOnly = false,
		acc: Map<string, GridBox> = new Map<string, GridBox>()
	): Map<string, GridBox> {
		const parent = this.sceneItems.get(parentId);
		if (parent) {
			const children = this.connectionManager.getChildren(parentId);
			const parentIsChild = this.connectionManager.isChildOnly(parent.articleId);
			for (const childId of children) {
				const childItem = this.sceneItems.get(childId);
				if (childItem) {
					const { transform, articleId } = childItem;
					if (articleId == 'leg') {
						continue;
					}
					if (includeChildOnly && !this.connectionManager.isChildOnly(articleId)) {
						continue;
					}
					const threshold = parentIsChild ? Thresholds.deepChildOnly : Thresholds.child;

					const childWorldTransform = this.connectionManager.getRelativeWorldTransform(
						transform,
						worldTransform
					);
					const boundsName = this.gridTools.getBoundsName(articleId);
					if (boundsName) {
						const childBox = this.gridTools.createGridBox(
							articleId,
							childWorldTransform,
							boundsName,
							threshold
						);
						acc.set(childId, childBox);
						if (this.connectionManager.hasChildren(childId)) {
							this.getChildrenGridBoxes(childId, childWorldTransform, includeChildOnly, acc);
						}
					}
				}
			}
		}
		return acc;
	}

	private getAutoObjectsGridBoxes(
		sceneItems: Map<string, ConnectableObject>,
		mockItems: Array<ConnectableObject>,
		mockConnections: Array<ConnectedObjects>
	): Map<string, GridBox[]> {
		const autoObjects = new Map<string, GridBox[]>();
		const items = new Map<string, ConnectableObject>(sceneItems);
		mockItems.forEach((item) => items.set(item.id, item));

		this.autoProducts.forEach((productId) => {
			const childModel = this.connectionManager.getModelData(productId);
			items.forEach((sceneItem) => {
				const parentModel = this.connectionManager.getModelData(sceneItem.articleId);
				if (!childModel || !parentModel) {
					return;
				}
				const relations = this.connectionManager.getRelations(childModel, parentModel);
				relations.forEach((r) => {
					const isOccupied = this.isOccupided(sceneItem.id, r);
					const isOccupidedByMock = this.isOccupidedByMock(sceneItem.id, r, mockConnections);
					if (isOccupied || isOccupidedByMock) {
						return;
					}
					const worldTransform = this.connectionManager.getConnectedWorldTransform(
						productId,
						sceneItem,
						r
					);
					const gridBox = this.gridTools.createGridBox(productId, worldTransform, 'default', 0.15);
					if (autoObjects.has(sceneItem.id)) {
						const autoObject = autoObjects.get(sceneItem.id);
						if (autoObject) {
							autoObject.push(gridBox);
						}
					} else {
						autoObjects.set(sceneItem.id, [gridBox]);
					}
				});
			});
		});
		return autoObjects;
	}

	private isOccupided(parentId: string, relation: ConnectionRelation): boolean {
		const object = this.sceneItems.get(parentId);
		if (object) {
			const availableConnections = this.moduleManager.getAvailableConnectionPoints(object);
			return !availableConnections.includes(relation.parent);
		}
		return false;
	}

	private isOccupidedByMock(
		parentId: string,
		relation: ConnectionRelation,
		mockConnections: Array<ConnectedObjects>
	): boolean {
		return mockConnections.some((conn) => {
			return (
				(conn.parentId === parentId && relation.parent === conn.relation.parent) ||
				(conn.childId === parentId && relation.parent === conn.relation.child)
			);
		});
	}

	private isAutoObject(sceneObject: ConnectableObject): boolean {
		const model = this.connectionManager.getModelData(sceneObject.articleId);
		if (
			model &&
			model.logic &&
			model.logic.autoConnect &&
			model.logic.autoConnectMovable !== true
		) {
			return model.logic.autoConnect;
		}
		return false;
	}

	private getArticlePositionY(articleId: string): number {
		const model = this.connectionManager.getModelData(articleId);
		if (!model) {
			return 0;
		}
		const size = model.bounds['default'].size;
		return size.height / 2;
	}
}
